import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
} from "reactstrap";
import { Puff } from "react-loader-spinner";
import {
  ConnectedStore,
  ConnectedStoreAddress,
} from "models/ConnectedStore";
import {
  StoreDefaultService,
  StoreDeliveryService,
  StorePricingCatalog,
} from "models/StoreDeliveryService";
import { AdminService } from "services/AdminService";
import { toast } from "react-toastify";
import PlatformBadge from "./PlatformBadge";

const deliveryMethodLabels: Record<string, string> = {
  drop_at_door: "Drop at Door",
  drop_at_mailbox: "Drop at Mailbox",
  drop_at_front_desk: "Drop at Front Desk",
  return: "Return if Delivery Fails",
  reattempt: "Reattempt if Delivery Fails",
  call_me_to_confirm: "Call to Confirm",
};

const EMPTY_CELL = "—";

const formatPickupAddress = (address?: ConnectedStoreAddress | null): string =>
  [
    address?.unit ? `Unit ${address.unit}` : "",
    address?.street,
    address?.city,
    address?.postalCode,
  ]
    .map(part => (part || "").trim())
    .filter(Boolean)
    .join(", ") || "Not set";

const displayTime = (value?: string): string => {
  if (!value || typeof value !== "string") return EMPTY_CELL;
  const match = value.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
  if (!match) return EMPTY_CELL;
  return `${match[1]}:${match[2]}`;
};

const displayWindow = (start?: string, end?: string): string => {
  const from = displayTime(start);
  const to = displayTime(end);
  if (from === EMPTY_CELL || to === EMPTY_CELL) return EMPTY_CELL;
  return `${from} – ${to}`;
};

const money = (value?: number): string =>
  new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(Number.isFinite(value) ? Number(value) : 0);

const errorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === "object" && error !== null) {
    const details = error as {
      message?: unknown;
      response?: { data?: { message?: unknown } };
    };
    const apiMessage = details.response?.data?.message;
    if (typeof apiMessage === "string" && apiMessage.trim()) {
      return apiMessage;
    }
    if (typeof details.message === "string" && details.message.trim()) {
      return details.message;
    }
  }
  return fallback;
};

interface ShopProfile {
  name: string;
  myshopifyDomain: string;
}

const emptyCatalog = (): StorePricingCatalog => ({
  defaultServices: [],
  storeServices: [],
  hasActiveStoreServices: false,
});

const DetailItem: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Col md={6} className="mb-3">
    <div className="text-muted font-size-12">{label}</div>
    <div className="text-break">{children}</div>
  </Col>
);

const CustomServiceCard: React.FC<{ service: StoreDeliveryService }> = ({
  service,
}) => {
  const pricing = service.pricing;
  const areas = service.territoryAreas || [];
  const vehicles = pricing?.vehicleSurcharges;

  return (
    <div className="border rounded p-3 mb-3">
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
        <div>
          <h6 className="mb-1">{service.name?.trim() || "Unnamed service"}</h6>
          <div className="text-muted font-size-12">
            {service.serviceType || EMPTY_CELL}
            {" · "}
            Cutoff {displayTime(service.cutoffTime)}
            {" · "}
            {displayWindow(
              service.deliveryWindowStart,
              service.deliveryWindowEnd
            )}
          </div>
        </div>
        <Badge color="success" pill>
          Active
        </Badge>
      </div>

      {service.description?.trim() ? (
        <p className="text-muted font-size-13 mb-3" style={{ whiteSpace: "pre-line" }}>
          {service.description.trim()}
        </p>
      ) : null}

      <h6 className="font-size-13 mb-2">Pricing</h6>
      {!pricing ? (
        <p className="text-muted font-size-13">Pricing is not set.</p>
      ) : (
        <Row>
          <DetailItem label="Flat price">
            {pricing.flatPricingEnabled ? (
              <>
                <div>{money(pricing.flatPrice)}</div>
                <span className="text-muted d-block font-size-12">
                  Includes {pricing.includedDistanceKm} km ·{" "}
                  {money(pricing.additionalKmPrice)} / additional km
                </span>
              </>
            ) : (
              <span className="text-muted">Disabled</span>
            )}
          </DetailItem>
          <DetailItem label="Vehicle charges">
            {pricing.vehicleChargesEnabled ? (
              <>
                Car {money(vehicles?.car)} · SUV {money(vehicles?.suv)} ·
                Minivan {money(vehicles?.minivan)}
              </>
            ) : (
              <span className="text-muted">Disabled</span>
            )}
          </DetailItem>
          <DetailItem label="Weight charges">
            {pricing.weightChargesEnabled ? (
              <>
                {pricing.freeWeightUpToLb} lb free ·{" "}
                {money(pricing.additionalPricePerLb)} / lb
              </>
            ) : (
              <span className="text-muted">Disabled</span>
            )}
          </DetailItem>
          <DetailItem label="Tax">
            {pricing.taxEnabled ? (
              Number.isFinite(pricing.hst) ? `HST ${pricing.hst}%` : "On"
            ) : (
              <span className="text-muted">Disabled</span>
            )}
          </DetailItem>
        </Row>
      )}

      <h6 className="font-size-13 mb-2">
        Territory
        {service.territoryName?.trim()
          ? ` · ${service.territoryName.trim()}`
          : ""}
      </h6>
      {areas.length === 0 ? (
        <p className="text-muted font-size-13 mb-0">No areas selected.</p>
      ) : (
        <div className="table-responsive border rounded">
          <Table className="align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Area / City</th>
                <th>Postal codes</th>
                <th className="text-end">Surcharge</th>
              </tr>
            </thead>
            <tbody>
              {areas.map(area => (
                <tr key={area.areaKey || area.label}>
                  <td className="fw-medium">
                    {area.label?.trim() || EMPTY_CELL}
                  </td>
                  <td style={{ whiteSpace: "normal" }}>
                    {(area.fsaCodes || []).filter(Boolean).join(", ") ||
                      EMPTY_CELL}
                  </td>
                  <td className="text-end">{money(area.surchargeCad)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

const StoreDetails: React.FC = () => {
  const { integrationId } = useParams<{ integrationId: string }>();
  const navigate = useNavigate();
  const [store, setStore] = useState<ConnectedStore | null>(null);
  const [profile, setProfile] = useState<ShopProfile | null>(null);
  const [catalog, setCatalog] = useState<StorePricingCatalog>(emptyCatalog());
  const [loading, setLoading] = useState(true);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [profileError, setProfileError] = useState("");
  const [servicesError, setServicesError] = useState("");

  const loadStore = useCallback(async () => {
    if (!integrationId) return;
    setLoading(true);
    setProfileError("");
    try {
      const response = await AdminService.getConnectedShopifyStore(integrationId);
      if (!response.success || !response.data) {
        throw new Error(response.message || "Unable to load store.");
      }
      setStore(response.data);
      if (response.data.platform?.toLowerCase() === "shopify") {
        try {
          const shop = await AdminService.getConnectedShopProfile(integrationId);
          if (!shop.success || !shop.data?.name) {
            throw new Error(
              shop.error ||
                shop.message ||
                "Unable to load the Shopify shop name."
            );
          }
          const shopName = shop.data.name;
          setProfile({
            name: shopName,
            myshopifyDomain: shop.data.myshopifyDomain || "",
          });
          setStore(current =>
            current ? { ...current, storeName: shopName } : current
          );
        } catch (error: unknown) {
          setProfile(null);
          setProfileError(
            errorMessage(error, "Unable to load the current Shopify shop name.")
          );
        }
      }
    } catch (error: unknown) {
      setStore(null);
      toast.error(errorMessage(error, "Unable to load connected store."));
    } finally {
      setLoading(false);
    }
  }, [integrationId]);

  const loadServices = useCallback(async () => {
    if (!integrationId) return;
    setServicesLoading(true);
    setServicesError("");
    try {
      const response = await AdminService.getStorePricingCatalog(integrationId);
      if (!response.success || !response.data) {
        throw new Error(
          response.message || response.error || "Unable to load store services."
        );
      }
      const data = response.data;
      if (Array.isArray(data)) {
        setCatalog({
          defaultServices: [],
          storeServices: data,
          hasActiveStoreServices: data.some(
            (service: StoreDeliveryService) => service.status === "active"
          ),
        });
      } else {
        setCatalog({
          defaultServices: data.defaultServices || [],
          storeServices: data.storeServices || [],
          hasActiveStoreServices: Boolean(data.hasActiveStoreServices),
        });
      }
    } catch (error: unknown) {
      setCatalog(emptyCatalog());
      setServicesError(
        errorMessage(error, "Unable to load activated store services.")
      );
    } finally {
      setServicesLoading(false);
    }
  }, [integrationId]);

  useEffect(() => {
    document.title = "Store Details | Zulu Courier";
    loadStore();
    loadServices();
  }, [loadStore, loadServices]);

  const activeStoreServices = useMemo(
    () =>
      (catalog.storeServices || []).filter(
        service => service.status === "active"
      ),
    [catalog.storeServices]
  );

  const enabledDefaultServices = useMemo(
    () =>
      (catalog.defaultServices || []).filter(
        (service: StoreDefaultService) => service.enabledForStore
      ),
    [catalog.defaultServices]
  );

  const hasActivatedServices =
    enabledDefaultServices.length > 0 || activeStoreServices.length > 0;

  if (loading) {
    return (
      <div className="page-content">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "500px" }}
        >
          <Puff color="#FFD0B8" height={50} width={50} />
        </div>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color="danger">Connected store could not be found.</Alert>
          <Button color="primary" onClick={() => navigate("/super_admin/stores")}>
            Back to Stores
          </Button>
        </Container>
      </div>
    );
  }

  const shopName = profile?.name || store.storeName;
  const deliveryMethod =
    deliveryMethodLabels[store.defaultDeliveryMethod || ""] ||
    store.defaultDeliveryMethod ||
    "Not set";

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
          <div className="d-flex align-items-center">
            <Button
              color="light"
              className="me-3"
              onClick={() => navigate("/super_admin/stores")}
            >
              <i className="bx bx-left-arrow-alt font-size-20" />
            </Button>
            <div>
              <h4 className="mb-1">Store Details</h4>
              <div className="text-muted">{shopName}</div>
            </div>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <PlatformBadge platform={store.platform} />
            <Badge color="success" pill>
              {store.connectionStatus}
            </Badge>
          </div>
        </div>

        <Card className="mb-4">
          <CardBody>
            {profileError && (
              <Alert color="warning">{profileError}</Alert>
            )}
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Shop name</Label>
                  <Input value={shopName} disabled />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Store domain</Label>
                  <Input
                    value={
                      profile?.myshopifyDomain ||
                      store.externalStoreDomain ||
                      store.externalStoreId ||
                      "Not set"
                    }
                    disabled
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Customer ID</Label>
                  <Input value={store.customer?.customerId || "Not linked"} disabled />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Customer name</Label>
                  <Input value={store.customer?.name || "Not linked"} disabled />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Notification email</Label>
                  <Input
                    value={store.customer?.email || "No customer account linked"}
                    disabled
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Contact phone</Label>
                  <Input
                    value={store.pickupAddress?.contactPhone || "Not set"}
                    disabled
                  />
                </FormGroup>
              </Col>
              <Col md={12}>
                <FormGroup>
                  <Label>Default pickup address</Label>
                  <Input value={formatPickupAddress(store.pickupAddress)} disabled />
                </FormGroup>
              </Col>
              <Col md={12}>
                <FormGroup className="mb-0">
                  <Label>Default delivery method</Label>
                  <Input value={deliveryMethod} disabled />
                </FormGroup>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <div>
                <h5 className="mb-1">Activated services</h5>
                <p className="text-muted font-size-13 mb-0">
                  Services currently offered for this shop at checkout
                </p>
              </div>
              {!servicesLoading && hasActivatedServices ? (
                <div className="d-flex flex-wrap gap-2">
                  <Badge color="primary" pill>
                    {enabledDefaultServices.length} default
                  </Badge>
                  <Badge color="success" pill>
                    {activeStoreServices.length} custom
                  </Badge>
                </div>
              ) : null}
            </div>

            {servicesLoading ? (
              <div className="d-flex justify-content-center py-5">
                <Puff color="#FFD0B8" height={40} width={40} />
              </div>
            ) : servicesError ? (
              <Alert color="warning" className="mb-0">
                {servicesError}
              </Alert>
            ) : !hasActivatedServices ? (
              <div className="text-center py-5 px-3">
                <div
                  className="avatar-md mx-auto mb-3"
                  aria-hidden
                >
                  <span className="avatar-title rounded-circle bg-light text-muted">
                    <i className="bx bx-package font-size-24" />
                  </span>
                </div>
                <h5 className="mb-2">No services activated</h5>
                <p
                  className="text-muted mx-auto mb-4"
                  style={{ maxWidth: 420 }}
                >
                  This shop does not have any default or custom delivery
                  services turned on yet. Activate services from Edit store
                  so customers can choose them at checkout.
                </p>
                <Button
                  color="primary"
                  onClick={() =>
                    navigate(`/super_admin/stores/${integrationId}/pricing`)
                  }
                >
                  <i className="bx bx-edit-alt me-1" />
                  Activate services
                </Button>
              </div>
            ) : (
              <>
                <h6 className="font-size-14 mb-2">Default Zulu services</h6>
                {enabledDefaultServices.length === 0 ? (
                  <p className="text-muted font-size-13 mb-4">
                    No default services are enabled for this store.
                  </p>
                ) : (
                  <div className="table-responsive border rounded mb-4">
                    <Table className="table-nowrap align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Service</th>
                          <th>Cutoff</th>
                          <th>Delivery window</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {enabledDefaultServices.map(service => (
                          <tr key={service.settingsType}>
                            <td>
                              <div className="fw-medium">
                                {service.name?.trim() || EMPTY_CELL}
                              </div>
                              {service.description?.trim() ? (
                                <div className="text-muted font-size-12">
                                  {service.description.trim()}
                                </div>
                              ) : null}
                            </td>
                            <td>{displayTime(service.cutoffTime)}</td>
                            <td>
                              {displayWindow(
                                service.deliveryWindowStart,
                                service.deliveryWindowEnd
                              )}
                            </td>
                            <td>
                              {service.globallyActive ? (
                                <Badge color="success" pill>
                                  Enabled
                                </Badge>
                              ) : (
                                <Badge color="warning" pill>
                                  Off in Settings
                                </Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}

                <h6 className="font-size-14 mb-2">Active custom services</h6>
                {activeStoreServices.length === 0 ? (
                  <p className="text-muted font-size-13 mb-0">
                    No custom services are currently active.
                  </p>
                ) : (
                  activeStoreServices.map((service, index) => (
                    <CustomServiceCard
                      key={String(service.id || service._id || index)}
                      service={service}
                    />
                  ))
                )}
              </>
            )}
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default StoreDetails;
